import {
  CameraHeatmap,
  CameraHeatmapOrderContribution,
  CameraHeatmapSegment,
} from '../interfaces/camera-heatmap.interface';

const DEFAULT_VISIBLE_START = 6;
const DEFAULT_VISIBLE_END = 22;

function sameCalendarDayUTC(a: Date, b: Date): boolean {
  return (
    a.getUTCDate() === b.getUTCDate() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCFullYear() === b.getUTCFullYear()
  );
}

function utcFractionalHour(d: Date): number {
  return d.getUTCHours() + d.getUTCMinutes() / 60 + d.getUTCSeconds() / 3600 + d.getUTCMilliseconds() / 3600000;
}

function occupancyPctToHeatmapColor(pct: number): string {
  const t = Math.min(100, Math.max(0, pct)) / 100;
  const hue = 120 * (1 - t);
  return `hsl(${hue}, 72%, 42%)`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPctKgAria(
  pct: number,
  kg: number,
  nfPct: Intl.NumberFormat,
  nfKg: Intl.NumberFormat
): string {
  return `${nfPct.format(pct)} por ciento, ${nfKg.format(kg)} kilogramos`;
}

function buildTooltipTableRow(
  label: string,
  pct: number,
  kg: number,
  nfPct: Intl.NumberFormat,
  nfKg: Intl.NumberFormat,
  isTotal = false
): string {
  const rowClass = isTotal ? ' class="total-row"' : '';
  return `<tr${rowClass}><th scope="row">${escapeHtml(label)}</th><td class="num">${nfPct.format(pct)} %</td><td class="num">${nfKg.format(kg)} kg</td></tr>`;
}

function buildSlotHoverLabel(
  slot: { occupancy_pct: number; kg_in_cameras: number; orders_contribution?: CameraHeatmapOrderContribution[] },
  nfPct: Intl.NumberFormat,
  nfKg: Intl.NumberFormat
): string {
  const rows: string[] = [];
  for (const order of slot.orders_contribution ?? []) {
    rows.push(buildTooltipTableRow(order.name, order.pct, order.kg, nfPct, nfKg));
  }
  rows.push(buildTooltipTableRow('Total', slot.occupancy_pct, slot.kg_in_cameras, nfPct, nfKg, true));
  return `<table class="camera-heatmap-tooltip-table"><tbody>${rows.join('')}</tbody></table>`;
}

function buildSlotAriaLabel(
  slot: { occupancy_pct: number; kg_in_cameras: number; orders_contribution?: CameraHeatmapOrderContribution[] },
  nfPct: Intl.NumberFormat,
  nfKg: Intl.NumberFormat
): string {
  let label = `Ocupación cámaras: ${formatPctKgAria(slot.occupancy_pct, slot.kg_in_cameras, nfPct, nfKg)}`;
  for (const order of slot.orders_contribution ?? []) {
    label += `. ${order.name}: ${formatPctKgAria(order.pct, order.kg, nfPct, nfKg)}`;
  }
  return label;
}

/**
 * Builds Gantt segments for camera heatmap slots on a given calendar day,
 * clipped to the visible window [visibleStartHour, visibleEndHour) in UTC.
 */
export function buildCameraHeatmapSegments(
  date: Date,
  cameraHeatmap: CameraHeatmap | null,
  isWorkingDay: (d: Date) => boolean,
  visibleStartHour = DEFAULT_VISIBLE_START,
  visibleEndHour = DEFAULT_VISIBLE_END
): CameraHeatmapSegment[] {
  if (!cameraHeatmap?.slots?.length || !isWorkingDay(date)) {
    return [];
  }
  const nfPct = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const nfKg = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const segments: CameraHeatmapSegment[] = [];

  for (const slot of cameraHeatmap.slots) {
    const slotStartDate = new Date(slot.start);
    const slotEndDate = new Date(slot.end);
    if (!sameCalendarDayUTC(slotStartDate, date)) {
      continue;
    }
    const slotStartH = utcFractionalHour(slotStartDate);
    const slotEndH = utcFractionalHour(slotEndDate);
    const clipStart = Math.max(slotStartH, visibleStartHour);
    const clipEnd = Math.min(slotEndH, visibleEndHour);
    if (clipEnd <= clipStart) {
      continue;
    }
    const gridColumnStart = Math.trunc((clipStart - visibleStartHour) * 60 + 2);
    const span = Math.max(1, Math.trunc((clipEnd - clipStart) * 60));
    const hoverLabel = buildSlotHoverLabel(slot, nfPct, nfKg);
    const ariaLabel = buildSlotAriaLabel(slot, nfPct, nfKg);
    segments.push({
      gridColumnStart,
      span,
      color: occupancyPctToHeatmapColor(slot.occupancy_pct),
      hoverLabel,
      ariaLabel,
    });
  }
  return segments;
}

/** Normalizes GET planificador / logistics payloads: array or `{ orders: … }`. */
export function flattenOrdersFromLogisticsResponse(res: unknown): any[] {
  if (res == null) {
    return [];
  }
  if (Array.isArray(res)) {
    return res;
  }
  if (typeof res !== 'object') {
    return [];
  }
  const orders = (res as { orders?: unknown }).orders;
  if (orders == null) {
    return [];
  }
  if (Array.isArray(orders)) {
    return orders as any[];
  }
  return (Object.values(orders) as any[][]).flat();
}

export function extractCameraHeatmapFromLogisticsResponse(res: unknown): CameraHeatmap | null {
  if (res == null || typeof res !== 'object' || Array.isArray(res)) {
    return null;
  }
  return ((res as { camera_heatmap?: CameraHeatmap | null }).camera_heatmap ?? null) as CameraHeatmap | null;
}
